import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { logger } from '../utils/logger';
import './AdminPage.css';

const API_BASE = import.meta.env.VITE_API_URL || '';
const BLOG_URL = import.meta.env.VITE_BLOG_URL || 'https://blog.raisa.chat';

// =============================================================================
// TYPES
// =============================================================================

interface AdminUser {
    id: string;
    email: string;
    adminRole: {
        role: 'moderator' | 'admin' | 'superadmin';
    } | null;
}

interface DashboardStats {
    users: {
        total: number;
        paid: number;
        free: number;
    };
    trips: {
        total: number;
        today: number;
        thisWeek: number;
        thisMonth: number;
    };
    content: {
        itineraries: { draft: number; published: number };
        cityGuides: { draft: number; published: number };
    };
}

interface FinancialStats {
    kpis: {
        activeSubscriptions: number;
        tripPurchases: number;
        mrr: number;
        arr: number;
        totalRevenue: number;
        subscriptionRevenue: number;
        tripRevenue: number;
        monthlyGrowth: number;
    };
    chartData: Array<{
        month: string;
        label: string;
        subscription: number;
        trip: number;
        total: number;
    }>;
}

interface SeoPage {
    id: string;
    tripId: string;
    locale: string;
    canonicalSlug: string;
    metaTitle: string;
    metaDescription: string;
    status: 'draft' | 'published' | 'archived';
    noindex: boolean;
    createdAt: string;
    trip: { slug: string };
}

interface UserListItem {
    id: string;
    email: string;
    name: string | null;
    createdAt: string;
    subscriptionStatus: 'active' | 'canceled' | 'past_due' | 'none';
    subscriptionEnd: string | null;
    tripsCount: number;
    purchasedTripsCount: number;
}

interface CityGuide {
    id: string;
    slug: string;
    name: string;
    type: 'city' | 'country';
    locale: string;
    status: 'draft' | 'published' | 'archived';
    itineraryCount: number;
}

// =============================================================================
// ICONS
// =============================================================================

const DashboardIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
);

const ItineraryIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
);

const CityGuideIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
);

const UsersIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const TripIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
    </svg>
);

const HomeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
);

const MenuIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const MoneyIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
);

const CreditCardIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
);

const TrendingUpIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
    </svg>
);

const ShoppingCartIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function AdminPage() {
    const navigate = useNavigate();
    
    // Auth state
    const [user, setUser] = useState<AdminUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    
    // Navigation state
    const [activeView, setActiveView] = useState<'dashboard' | 'itineraries' | 'cityguides' | 'users'>('dashboard');
    
    // Dashboard state
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [financialStats, setFinancialStats] = useState<FinancialStats | null>(null);
    
    // Content state
    const [seoPages, setSeoPages] = useState<SeoPage[]>([]);
    const [cityGuides, setCityGuides] = useState<CityGuide[]>([]);
    const [filter, setFilter] = useState<'all' | 'draft' | 'published'>('draft');
    
    // Users state
    const [usersList, setUsersList] = useState<UserListItem[]>([]);
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotalPages, setUsersTotalPages] = useState(1);
    const [usersSearch, setUsersSearch] = useState('');
    
    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [selectedPage, setSelectedPage] = useState<SeoPage | null>(null);
    const [editedMeta, setEditedMeta] = useState({ title: '', description: '' });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // =============================================================================
    // AUTH CHECK
    // =============================================================================
    
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/user/me`, { credentials: 'include' });
                if (!res.ok) {
                    navigate({ to: '/' } as any);
                    return;
                }
                
                const data = await res.json();
                setUser(data.user);
                
                if (data.user.adminRole && ['admin', 'superadmin'].includes(data.user.adminRole.role)) {
                    setIsAuthorized(true);
                } else {
                    navigate({ to: '/' } as any);
                }
            } catch {
                navigate({ to: '/' } as any);
            } finally {
                setIsLoading(false);
            }
        };
        
        checkAuth();
    }, [navigate]);

    // =============================================================================
    // DATA FETCHING
    // =============================================================================

    useEffect(() => {
        if (!isAuthorized) return;
        
        const fetchStats = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/admin/dashboard/stats`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setStats(data);
                }
            } catch (err) {
                logger.error('Failed to fetch stats:', err);
            }
        };

        const fetchFinancialStats = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/admin/dashboard/financial`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setFinancialStats(data);
                }
            } catch (err) {
                logger.error('Failed to fetch financial stats:', err);
            }
        };
        
        fetchStats();
        fetchFinancialStats();
    }, [isAuthorized]);

    useEffect(() => {
        if (!isAuthorized || activeView !== 'itineraries') return;
        
        const fetchPages = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/admin/seo-queue?status=${filter}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setSeoPages(data.pages || []);
                }
            } catch (err) {
                logger.error('Failed to fetch SEO pages:', err);
            }
        };
        
        fetchPages();
    }, [isAuthorized, activeView, filter]);

    useEffect(() => {
        if (!isAuthorized || activeView !== 'cityguides') return;
        
        const fetchGuides = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/admin/city-guides?status=${filter}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setCityGuides(data.guides || []);
                }
            } catch (err) {
                logger.error('Failed to fetch city guides:', err);
            }
        };
        
        fetchGuides();
    }, [isAuthorized, activeView, filter]);

    // Fetch users when users view is active
    useEffect(() => {
        if (!isAuthorized || activeView !== 'users') return;
        
        const fetchUsers = async () => {
            try {
                const params = new URLSearchParams({
                    page: usersPage.toString(),
                    limit: '50',
                    ...(usersSearch && { search: usersSearch }),
                });
                const res = await fetch(`${API_BASE}/api/admin/users?${params}`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setUsersList(data.users || []);
                    setUsersTotalPages(data.pagination?.totalPages || 1);
                }
            } catch (err) {
                logger.error('Failed to fetch users:', err);
            }
        };
        
        fetchUsers();
    }, [isAuthorized, activeView, usersPage, usersSearch]);

    // =============================================================================
    // ACTIONS
    // =============================================================================

    const handlePromote = async (pageId: string) => {
        if (!confirm('This will make the trip publicly indexed. Continue?')) return;
        
        setIsSubmitting(true);
        setMessage(null);
        
        try {
            const res = await fetch(`${API_BASE}/api/admin/seo/${pageId}/promote`, {
                method: 'POST',
                credentials: 'include',
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Trip promoted successfully!' });
                setSeoPages(pages => pages.filter(p => p.id !== pageId));
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Failed to promote' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePublishToBlog = async (tripId: string) => {
        if (!confirm('This will generate enhanced SEO content and city guides. Continue?')) return;
        
        setIsSubmitting(true);
        setMessage(null);
        
        try {
            const res = await fetch(`${API_BASE}/api/admin/trips/${tripId}/publish-to-blog`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ locales: ['en', 'ro'] }),
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessage({ type: 'success', text: `Publishing started! Job ID: ${data.jobId}` });
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Failed to publish' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePublishCityGuide = async (guideId: string) => {
        if (!confirm('Publish this city guide?')) return;
        
        setIsSubmitting(true);
        setMessage(null);
        
        try {
            const res = await fetch(`${API_BASE}/api/admin/city-guides/${guideId}/publish`, {
                method: 'POST',
                credentials: 'include',
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'City guide published!' });
                setCityGuides(guides => guides.map(g => 
                    g.id === guideId ? { ...g, status: 'published' as const } : g
                ));
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Failed to publish' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveMeta = async () => {
        if (!selectedPage) return;
        
        setIsSubmitting(true);
        setMessage(null);
        
        try {
            const res = await fetch(`${API_BASE}/api/admin/seo/${selectedPage.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    metaTitle: editedMeta.title,
                    metaDescription: editedMeta.description,
                }),
            });
            
            if (res.ok) {
                setMessage({ type: 'success', text: 'Meta updated!' });
                setSeoPages(pages => pages.map(p => 
                    p.id === selectedPage.id 
                        ? { ...p, metaTitle: editedMeta.title, metaDescription: editedMeta.description }
                        : p
                ));
                setSelectedPage(null);
            } else {
                const data = await res.json();
                setMessage({ type: 'error', text: data.error || 'Failed to update' });
            }
        } catch {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // =============================================================================
    // LOADING STATE
    // =============================================================================

    if (isLoading) {
        return (
            <div className="admin-loading-screen">
                <div className="loading-spinner" />
                <p>Loading admin panel...</p>
            </div>
        );
    }

    if (!isAuthorized) return null;

    // =============================================================================
    // RENDER
    // =============================================================================

    // Handle navigation click on mobile - close menu after selecting
    const handleNavClick = (view: typeof activeView) => {
        setActiveView(view);
        setMobileMenuOpen(false);
    };

    return (
        <div className="admin-layout">
            {/* Mobile Overlay */}
            {mobileMenuOpen && (
                <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <img src="/raisa.png" alt="Raisa" className="sidebar-logo" />
                    <span className="sidebar-title">Admin</span>
                    <button className="mobile-close-btn" onClick={() => setMobileMenuOpen(false)}>
                        <CloseIcon />
                    </button>
                </div>
                
                <nav className="sidebar-nav">
                    <button 
                        className={`nav-item ${activeView === 'dashboard' ? 'active' : ''}`}
                        onClick={() => handleNavClick('dashboard')}
                    >
                        <DashboardIcon />
                        <span>Dashboard</span>
                    </button>
                    <button 
                        className={`nav-item ${activeView === 'itineraries' ? 'active' : ''}`}
                        onClick={() => handleNavClick('itineraries')}
                    >
                        <ItineraryIcon />
                        <span>Itineraries</span>
                        {stats?.content.itineraries.draft ? (
                            <span className="nav-badge">{stats.content.itineraries.draft}</span>
                        ) : null}
                    </button>
                    <button 
                        className={`nav-item ${activeView === 'cityguides' ? 'active' : ''}`}
                        onClick={() => handleNavClick('cityguides')}
                    >
                        <CityGuideIcon />
                        <span>City Guides</span>
                        {stats?.content.cityGuides.draft ? (
                            <span className="nav-badge">{stats.content.cityGuides.draft}</span>
                        ) : null}
                    </button>
                    <button 
                        className={`nav-item ${activeView === 'users' ? 'active' : ''}`}
                        onClick={() => handleNavClick('users')}
                    >
                        <UsersIcon />
                        <span>Users</span>
                        {stats?.users.total ? (
                            <span className="nav-badge">{stats.users.total}</span>
                        ) : null}
                    </button>
                </nav>
                
                <div className="sidebar-footer">
                    <a href="/" className="nav-item">
                        <HomeIcon />
                        <span>Back to App</span>
                    </a>
                    <div className="user-info">
                        <div className="user-avatar">
                            {user?.email?.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                            <span className="user-email">{user?.email}</span>
                            <span className="user-role">{user?.adminRole?.role}</span>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="admin-main">
                {/* Top Bar */}
                <header className="admin-topbar">
                    <div className="topbar-left">
                        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>
                            <MenuIcon />
                        </button>
                        <h1 className="page-title">
                            {activeView === 'dashboard' && 'Dashboard'}
                            {activeView === 'itineraries' && 'Itineraries'}
                            {activeView === 'cityguides' && 'City Guides'}
                            {activeView === 'users' && 'Users'}
                        </h1>
                    </div>
                    <div className="topbar-actions">
                        <a href={BLOG_URL} target="_blank" rel="noopener noreferrer" className="topbar-link">
                            View Blog →
                        </a>
                    </div>
                </header>

                {/* Messages */}
                {message && (
                    <div className={`admin-toast ${message.type}`}>
                        {message.text}
                        <button onClick={() => setMessage(null)}>×</button>
                    </div>
                )}

                {/* Content Area */}
                <div className="admin-content">
                    {/* Dashboard View */}
                    {activeView === 'dashboard' && stats && (
                        <div className="dashboard-grid">
                            {/* Users Stats */}
                            <div className="stat-card users">
                                <div className="stat-icon"><UsersIcon /></div>
                                <div className="stat-content">
                                    <h3>Total Users</h3>
                                    <p className="stat-value">{stats.users.total}</p>
                                    <div className="stat-breakdown">
                                        <span className="stat-paid">{stats.users.paid} paid</span>
                                        <span className="stat-free">{stats.users.free} free</span>
                                    </div>
                                </div>
                            </div>

                            {/* Trips Stats */}
                            <div className="stat-card trips">
                                <div className="stat-icon"><TripIcon /></div>
                                <div className="stat-content">
                                    <h3>Total Trips</h3>
                                    <p className="stat-value">{stats.trips.total}</p>
                                    <div className="stat-breakdown">
                                        <span>{stats.trips.today} today</span>
                                        <span>{stats.trips.thisWeek} this week</span>
                                    </div>
                                </div>
                            </div>

                            {/* Itineraries Stats */}
                            <div className="stat-card itineraries">
                                <div className="stat-icon"><ItineraryIcon /></div>
                                <div className="stat-content">
                                    <h3>Itineraries</h3>
                                    <p className="stat-value">{stats.content.itineraries.draft + stats.content.itineraries.published}</p>
                                    <div className="stat-breakdown">
                                        <span className="stat-draft">{stats.content.itineraries.draft} draft</span>
                                        <span className="stat-published">{stats.content.itineraries.published} published</span>
                                    </div>
                                </div>
                            </div>

                            {/* City Guides Stats */}
                            <div className="stat-card guides">
                                <div className="stat-icon"><CityGuideIcon /></div>
                                <div className="stat-content">
                                    <h3>City Guides</h3>
                                    <p className="stat-value">{stats.content.cityGuides.draft + stats.content.cityGuides.published}</p>
                                    <div className="stat-breakdown">
                                        <span className="stat-draft">{stats.content.cityGuides.draft} draft</span>
                                        <span className="stat-published">{stats.content.cityGuides.published} published</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Financial Stats Section */}
                    {activeView === 'dashboard' && financialStats && (
                        <>
                            <h2 className="section-title">Financial KPIs</h2>
                            <div className="dashboard-grid financial-grid">
                                {/* Active Subscriptions */}
                                <div className="stat-card premium">
                                    <div className="stat-icon"><CreditCardIcon /></div>
                                    <div className="stat-content">
                                        <h3>Premium Subscriptions</h3>
                                        <p className="stat-value">{financialStats.kpis.activeSubscriptions}</p>
                                        <div className="stat-breakdown">
                                            <span className="stat-highlight">€{financialStats.kpis.subscriptionRevenue.toFixed(2)} total</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Trip Purchases */}
                                <div className="stat-card purchases">
                                    <div className="stat-icon"><ShoppingCartIcon /></div>
                                    <div className="stat-content">
                                        <h3>Trip Purchases</h3>
                                        <p className="stat-value">{financialStats.kpis.tripPurchases}</p>
                                        <div className="stat-breakdown">
                                            <span className="stat-highlight">€{financialStats.kpis.tripRevenue.toFixed(2)} total</span>
                                        </div>
                                    </div>
                                </div>

                                {/* MRR */}
                                <div className="stat-card mrr">
                                    <div className="stat-icon"><TrendingUpIcon /></div>
                                    <div className="stat-content">
                                        <h3>MRR</h3>
                                        <p className="stat-value">€{financialStats.kpis.mrr.toFixed(2)}</p>
                                        <div className="stat-breakdown">
                                            <span className={`stat-growth ${financialStats.kpis.monthlyGrowth >= 0 ? 'positive' : 'negative'}`}>
                                                {financialStats.kpis.monthlyGrowth >= 0 ? '+' : ''}{financialStats.kpis.monthlyGrowth}% this month
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* ARR */}
                                <div className="stat-card arr">
                                    <div className="stat-icon"><MoneyIcon /></div>
                                    <div className="stat-content">
                                        <h3>ARR</h3>
                                        <p className="stat-value">€{financialStats.kpis.arr.toFixed(2)}</p>
                                        <div className="stat-breakdown">
                                            <span className="stat-highlight">€{financialStats.kpis.totalRevenue.toFixed(2)} lifetime</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Revenue Chart */}
                            <div className="revenue-chart-container">
                                <h3>Monthly Revenue (Last 12 Months)</h3>
                                <div className="revenue-chart">
                                    {financialStats.chartData.map((month) => {
                                        const maxRevenue = Math.max(...financialStats.chartData.map(m => m.total), 1);
                                        const height = (month.total / maxRevenue) * 100;
                                        
                                        return (
                                            <div key={month.month} className="chart-bar-wrapper">
                                                <div className="chart-bar" style={{ height: `${Math.max(height, 2)}%` }}>
                                                    <div 
                                                        className="bar-subscription" 
                                                        style={{ height: `${month.total > 0 ? (month.subscription / month.total) * 100 : 0}%` }}
                                                        title={`Subscriptions: €${month.subscription.toFixed(2)}`}
                                                    />
                                                    <div 
                                                        className="bar-trip" 
                                                        style={{ height: `${month.total > 0 ? (month.trip / month.total) * 100 : 0}%` }}
                                                        title={`Trip purchases: €${month.trip.toFixed(2)}`}
                                                    />
                                                </div>
                                                <span className="chart-label">{month.label}</span>
                                                <span className="chart-value">€{month.total.toFixed(0)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="chart-legend">
                                    <span className="legend-item subscription"><span className="legend-dot" /> Subscriptions</span>
                                    <span className="legend-item trip"><span className="legend-dot" /> Trip Purchases</span>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Itineraries View */}
                    {activeView === 'itineraries' && (
                        <div className="content-view">
                            <div className="content-header">
                                <div className="filter-tabs">
                                    {(['draft', 'published', 'all'] as const).map(f => (
                                        <button 
                                            key={f}
                                            className={filter === f ? 'active' : ''} 
                                            onClick={() => setFilter(f)}
                                        >
                                            {f.charAt(0).toUpperCase() + f.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {seoPages.length === 0 ? (
                                <div className="empty-state">
                                    <ItineraryIcon />
                                    <p>No itineraries found</p>
                                </div>
                            ) : (
                                <div className="data-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Title</th>
                                                <th>Status</th>
                                                <th>Locale</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {seoPages.map(page => (
                                                <tr key={page.id}>
                                                    <td>
                                                        <div className="title-cell">
                                                            <span className="title">{page.metaTitle}</span>
                                                            <span className="desc">{page.metaDescription}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${page.status}`}>{page.status}</span>
                                                        {page.noindex && <span className="badge noindex">noindex</span>}
                                                    </td>
                                                    <td>{page.locale.toUpperCase()}</td>
                                                    <td>{new Date(page.createdAt).toLocaleDateString()}</td>
                                                    <td>
                                                        <div className="actions">
                                                            <button className="btn-sm" onClick={() => {
                                                                setSelectedPage(page);
                                                                setEditedMeta({ title: page.metaTitle, description: page.metaDescription });
                                                            }}>Edit</button>
                                                            {page.status === 'draft' && (
                                                                <button className="btn-sm btn-primary" onClick={() => handlePromote(page.id)} disabled={isSubmitting}>
                                                                    Promote
                                                                </button>
                                                            )}
                                                            <button className="btn-sm btn-accent" onClick={() => handlePublishToBlog(page.tripId)} disabled={isSubmitting}>
                                                                Publish
                                                            </button>
                                                            <a href={`/trip/${page.trip.slug}`} target="_blank" rel="noopener noreferrer" className="btn-sm">
                                                                View
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* City Guides View */}
                    {activeView === 'cityguides' && (
                        <div className="content-view">
                            <div className="content-header">
                                <div className="filter-tabs">
                                    {(['draft', 'published', 'all'] as const).map(f => (
                                        <button 
                                            key={f}
                                            className={filter === f ? 'active' : ''} 
                                            onClick={() => setFilter(f)}
                                        >
                                            {f.charAt(0).toUpperCase() + f.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {cityGuides.length === 0 ? (
                                <div className="empty-state">
                                    <CityGuideIcon />
                                    <p>No city guides found</p>
                                </div>
                            ) : (
                                <div className="data-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Type</th>
                                                <th>Locale</th>
                                                <th>Itineraries</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cityGuides.map(guide => (
                                                <tr key={guide.id}>
                                                    <td>
                                                        <div className="title-cell">
                                                            <span className="title">{guide.name}</span>
                                                            <span className="desc">/{guide.slug}</span>
                                                        </div>
                                                    </td>
                                                    <td><span className={`badge ${guide.type}`}>{guide.type}</span></td>
                                                    <td>{guide.locale.toUpperCase()}</td>
                                                    <td>{guide.itineraryCount}</td>
                                                    <td><span className={`badge ${guide.status}`}>{guide.status}</span></td>
                                                    <td>
                                                        <div className="actions">
                                                            {guide.status === 'draft' && (
                                                                <button className="btn-sm btn-primary" onClick={() => handlePublishCityGuide(guide.id)} disabled={isSubmitting}>
                                                                    Publish
                                                                </button>
                                                            )}
                                                            <a href={`${BLOG_URL}/${guide.locale}/city-guides/${guide.slug}`} target="_blank" rel="noopener noreferrer" className="btn-sm">
                                                                Preview
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Users View */}
                    {activeView === 'users' && (
                        <div className="content-view users-view">
                            <div className="content-header">
                                <div className="search-box">
                                    <input
                                        type="text"
                                        placeholder="Search by email or name..."
                                        value={usersSearch}
                                        onChange={e => {
                                            setUsersSearch(e.target.value);
                                            setUsersPage(1);
                                        }}
                                        className="search-input"
                                    />
                                </div>
                            </div>

                            {usersList.length === 0 ? (
                                <div className="empty-state">
                                    <UsersIcon />
                                    <p>No users found</p>
                                </div>
                            ) : (
                                <>
                                    <div className="data-table users-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>User</th>
                                                    <th>Subscription</th>
                                                    <th>Trips</th>
                                                    <th>Purchased</th>
                                                    <th>Joined</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {usersList.map(u => (
                                                    <tr key={u.id}>
                                                        <td>
                                                            <div className="user-cell">
                                                                <div className="user-avatar-small">
                                                                    {u.email?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div className="user-info-cell">
                                                                    <span className="user-name">{u.name || '—'}</span>
                                                                    <span className="user-email">{u.email}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${u.subscriptionStatus === 'active' ? 'published' : u.subscriptionStatus === 'canceled' ? 'archived' : 'draft'}`}>
                                                                {u.subscriptionStatus === 'none' ? 'Free' : u.subscriptionStatus}
                                                            </span>
                                                            {u.subscriptionEnd && u.subscriptionStatus === 'active' && (
                                                                <span className="sub-info">
                                                                    until {new Date(u.subscriptionEnd).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{u.tripsCount}</td>
                                                        <td>{u.purchasedTripsCount}</td>
                                                        <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {usersTotalPages > 1 && (
                                        <div className="pagination">
                                            <button 
                                                className="btn-sm"
                                                onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                                                disabled={usersPage === 1}
                                            >
                                                ← Prev
                                            </button>
                                            <span className="page-info">
                                                Page {usersPage} of {usersTotalPages}
                                            </span>
                                            <button 
                                                className="btn-sm"
                                                onClick={() => setUsersPage(p => Math.min(usersTotalPages, p + 1))}
                                                disabled={usersPage === usersTotalPages}
                                            >
                                                Next →
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* Edit Meta Modal */}
            {selectedPage && (
                <div className="modal-overlay" onClick={() => setSelectedPage(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Edit Meta Tags</h3>
                        <div className="form-group">
                            <label>Meta Title</label>
                            <input
                                type="text"
                                value={editedMeta.title}
                                onChange={e => setEditedMeta({ ...editedMeta, title: e.target.value })}
                                maxLength={70}
                            />
                            <span className="char-count">{editedMeta.title.length}/70</span>
                        </div>
                        <div className="form-group">
                            <label>Meta Description</label>
                            <textarea
                                value={editedMeta.description}
                                onChange={e => setEditedMeta({ ...editedMeta, description: e.target.value })}
                                maxLength={160}
                                rows={3}
                            />
                            <span className="char-count">{editedMeta.description.length}/160</span>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setSelectedPage(null)}>Cancel</button>
                            <button className="btn-primary" onClick={handleSaveMeta} disabled={isSubmitting}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
